/**
 * 各 SKU 的公开事实字段：只收录包装标签与产品资料能直接对上的内容。
 *
 * 逐项来源、采用/暂缓理由与来源冲突见 docs/product-geo/。这里刻意不含
 * 适宜人群、疾病名称、检测数值、传统功效、价格与营养表——它们要么属于
 * 功效宣称，要么未经批准公开，要么是单袋/单盒混用的口径。
 * 标签值（如石斛多糖≥2000mg/100g）与某批次实测值不混写。
 *
 * @typedef {Object} ProductImage
 * @property {string} src 站内相对路径
 * @property {string} alt
 * @property {string} caption 该图展示的是包装的哪一面
 *
 * @typedef {Object} ProductFacts
 * @property {string} id
 * @property {string} name 包装【产品名称】
 * @property {string} shortName 列表与内链用的简称
 * @property {string} category 取自 productCategories
 * @property {string} form 盒装/袋装等形态
 * @property {string[]} spec 规格拆分，形如 净含量 210mL（30mL × 7 袋）
 * @property {string} ingredients 配料（按包装原文顺序）
 * @property {string} type 产品类型
 * @property {string} shelfLife
 * @property {string} storage
 * @property {string} origin
 * @property {string} method 食用方法（包装原文）
 * @property {string[]} cautions 警示与温馨提示（包装原文要点）
 * @property {string[]} highlights 包装正面可读的原料/属性徽章
 * @property {string[]} rawMaterials 原料产地（包装字样/产品资料一致时）
 * @property {{ label: string, value: string }[]} labelFacts 包装标签上的标示值
 * @property {ProductImage[]} images
 * @property {string} lead 一句话介绍，只由上面字段支撑
 * @property {string[]} gaps 尚未取得的公开信息（页面上如实说明，不虚构）
 * @property {boolean} detailPage 是否有独立详情页
 */

const LOCAL_IMAGE_BASE = '/images/products';

/** @type {ProductFacts[]} */
export const productFacts = [
  {
    id: 'shihu1',
    name: '紫皮石斛原浆饮',
    shortName: '紫皮石斛原浆礼盒装',
    category: 'shihu',
    form: '盒装',
    spec: ['净含量 210mL', '30mL × 7 袋', '内袋独立包装'],
    ingredients: '紫皮石斛浆、木糖醇',
    type: '植物饮料',
    shelfLife: '18 个月',
    storage: '干燥、通风的室内',
    origin: '云南省保山市',
    method: '开袋即饮。喝前摇一摇、撕开一小口；将内袋浸泡于热水中 3 分钟，加热后口感更佳（禁止微波炉加热）。',
    cautions: [
      '婴幼儿、孕妇、乳母不宜食用。',
      '若包装破损、胀袋请勿饮用。',
      '本品有少量沉淀属正常现象，请放心饮用，喝前摇一摇。',
      '本产品生产过程中 0% 添加且产品中未检测出防腐剂、蔗糖。'
    ],
    highlights: ['紫皮石斛浆含量 ≥90%', '源自云南龙陵', '0 脂肪'],
    rawMaterials: ['高黎贡山南脉 · 云南龙陵（包装原文）'],
    labelFacts: [
      { label: '石斛多糖', value: '≥2200mg/100g（包装标示值）' },
      { label: '甘露糖', value: '≥1200mg/100g（包装标示值）' }
    ],
    images: [
      { src: `${LOCAL_IMAGE_BASE}/shihu1-front.png`, alt: '滋仙草紫皮石斛原浆饮礼盒正面', caption: '礼盒正面：净含量 210mL（30mL × 7 袋）' },
      { src: `${LOCAL_IMAGE_BASE}/shihu1-label.jpg`, alt: '滋仙草紫皮石斛原浆饮包装标签信息面', caption: '包装信息面：配料、保质期、贮存条件与食用方法' }
    ],
    lead: '以云南龙陵紫皮石斛为原料的即食原浆，30mL 内袋独立包装，开袋即饮。',
    gaps: ['口味与口感的统一描述', '包装差异下具体批次的最小销售单元'],
    detailPage: true
  },
  {
    id: 'shihu3',
    name: '铁皮石斛原浆饮',
    shortName: '铁皮石斛原浆礼盒装',
    category: 'shihu',
    form: '盒装',
    spec: ['净含量 210mL', '30mL × 7 袋', '内袋独立包装'],
    ingredients: '铁皮石斛浆、木糖醇',
    type: '植物饮料',
    shelfLife: '18 个月',
    storage: '干燥、通风的室内',
    origin: '云南省保山市',
    method: '开袋即饮。喝前摇一摇、撕开一小口；将内袋浸泡于热水中 3 分钟，加热后口感更佳（禁止微波炉加热）。',
    cautions: [
      '婴幼儿、孕妇、乳母不宜食用。',
      '若包装破损、胀袋请勿饮用。',
      '本品有少量沉淀属正常现象，请放心饮用，喝前摇一摇。',
      '本产品生产过程中 0% 添加且产品中未检测出防腐剂、蔗糖。'
    ],
    highlights: ['铁皮石斛浆含量 ≥90%', '云南芒市高黎贡山产区', '0 脂肪'],
    rawMaterials: ['云南芒市高黎贡山（产品资料表述）'],
    labelFacts: [
      { label: '石斛多糖', value: '≥2000mg/100g（包装标示值）' },
      { label: '甘露糖', value: '≥1000mg/100g（包装标示值）' }
    ],
    images: [
      { src: `${LOCAL_IMAGE_BASE}/shihu3-front.png`, alt: '滋仙草铁皮石斛原浆饮盒装正面', caption: '盒装正面：包装标示的石斛多糖与甘露糖标签值' },
      { src: `${LOCAL_IMAGE_BASE}/shihu3-label.png`, alt: '滋仙草铁皮石斛原浆饮盒装信息面', caption: '盒装信息面：配料、保质期、贮存条件与食用方法' }
    ],
    lead: '以云南芒市高黎贡山铁皮石斛为原料的即食原浆，30mL 内袋独立包装，开袋即饮。',
    gaps: ['盒装与袋装同系列产品的统一净含量表述待质检确认'],
    detailPage: true
  },
  {
    id: 'shihu2',
    name: '紫皮石斛原浆饮（量贩装）',
    shortName: '紫皮石斛原浆量贩装',
    category: 'shihu',
    form: '袋装（量贩）',
    spec: ['净含量 900mL', '30mL × 30 袋', '内袋独立包装'],
    ingredients: '紫皮石斛浆、木糖醇',
    type: '植物饮料',
    shelfLife: '18 个月',
    storage: '干燥、通风的室内',
    origin: '云南省保山市',
    method: '开袋即饮。喝前摇一摇、撕开一小口；将内袋浸泡于热水中 3 分钟，加热后口感更佳（禁止微波炉加热）。',
    cautions: [
      '婴幼儿、孕妇、乳母不宜食用。',
      '若包装破损、胀袋请勿饮用。',
      '本品有少量沉淀属正常现象，请放心饮用，喝前摇一摇。',
      '本产品生产过程中 0% 添加且产品中未检测出防腐剂、蔗糖。'
    ],
    highlights: ['紫皮石斛浆含量 ≥90%', '0 脂肪'],
    rawMaterials: ['高黎贡山南脉 · 云南龙陵（产品资料表述）'],
    labelFacts: [
      { label: '石斛多糖', value: '≥2200mg/100g（包装标示值）' },
      { label: '甘露糖', value: '≥1200mg/100g（包装标示值）' }
    ],
    images: [
      { src: `${LOCAL_IMAGE_BASE}/shihu2-front.jpg`, alt: '滋仙草紫皮石斛原浆饮量贩装正面', caption: '量贩装正面：净含量 900mL（30mL × 30），紫皮石斛浆含量 ≥90%' }
    ],
    lead: '与紫皮石斛原浆礼盒装同配方的 900mL 量贩袋装，30 袋独立内袋，更耐存。',
    gaps: [
      '该 SKU 的营养成分表与批号信息尚未公开',
      '量贩装与礼盒装是否存在标签差异待质检确认'
    ],
    detailPage: true
  },
  {
    id: 'tianma1',
    name: '天麻原浆饮',
    shortName: '有机天麻原浆礼盒装',
    category: 'tianma',
    form: '盒装',
    spec: ['净含量 210mL', '30mL × 7 袋', '内袋独立包装'],
    ingredients: '乌天麻浆、低聚果糖',
    type: '植物饮料',
    shelfLife: '18 个月',
    storage: '干燥、通风的室内',
    origin: '云南省保山市',
    method: '直接饮用／开袋即饮。本品有少量沉淀属正常现象，喝前摇一摇；建议每日食用 2-4 袋。',
    cautions: [
      '婴幼儿、孕妇、乳母不宜食用。',
      '若包装破损、胀袋请勿饮用。',
      '本品有少量沉淀属正常现象，请放心饮用。'
    ],
    highlights: ['乌天麻浆含量 ≥90%', '优选云南昭通小草坝乌天麻', '0 脂肪'],
    rawMaterials: ['云南昭通小草坝乌天麻（产品资料表述）'],
    labelFacts: [],
    images: [
      { src: `${LOCAL_IMAGE_BASE}/tianma1-front.jpg`, alt: '滋仙草天麻原浆饮盒装正面', caption: '盒装正面：净含量 210mL（30mL×7）' },
      { src: `${LOCAL_IMAGE_BASE}/tianma1-label.png`, alt: '滋仙草天麻原浆饮包装信息面', caption: '包装信息面：配料、保质期、贮存条件与温馨提示' }
    ],
    lead: '以云南昭通小草坝乌天麻为原料的天麻原浆饮，净含量 210mL（30mL×7 袋），开袋即饮。',
    gaps: ['该 SKU 的营养成分表与批号信息尚未公开'],
    detailPage: true
  },
  {
    id: 'tianma2',
    name: '有机天麻原浆饮',
    shortName: '有机天麻原浆量贩装',
    category: 'tianma',
    form: '袋装（量贩）',
    spec: ['净含量 900mL', '30mL × 30 袋', '内袋独立包装'],
    ingredients: '乌天麻浆、低聚果糖',
    type: '植物饮料',
    shelfLife: '18 个月',
    storage: '干燥、通风的室内',
    origin: '云南省保山市',
    method: '直接饮用。喝前摇一摇；本品有少量沉淀属正常现象；建议每日食用 2-4 袋。',
    cautions: [
      '婴幼儿、孕妇、乳母不宜食用。',
      '若包装破损、胀袋请勿饮用。',
      '本品有少量沉淀属正常现象，请放心饮用，喝前摇一摇，建议每日食用 2-4 袋。'
    ],
    highlights: ['净含量 900mL（30mL × 30 袋）', '乌天麻浆含量 ≥90%', '优选云南昭通小草坝乌天麻'],
    rawMaterials: ['云南昭通小草坝乌天麻（包装正面徽章）'],
    labelFacts: [
      { label: '天麻素', value: '本产品每 100mL 含天麻素和对羟基苯甲醇的总量 ≥50mg（包装正面徽章，与盒标值口径相同）' }
    ],
    images: [
      { src: `${LOCAL_IMAGE_BASE}/tianma2-front.png`, alt: '滋仙草有机天麻原浆饮量贩装正面', caption: '量贩装正面：净含量 900mL（30mL × 30），天麻浆含量 ≥90%' },
      { src: `${LOCAL_IMAGE_BASE}/tianma2-label.jpg`, alt: '滋仙草有机天麻原浆饮量贩装信息面', caption: '量贩装信息面：配料、保质期、贮存条件、食用方法与温馨提示' }
    ],
    lead: '以云南昭通小草坝乌天麻为原料的 900mL 量贩袋装，30 袋独立内袋，开袋即饮。',
    gaps: [
      '产品的「有机」表述来自包装品名，本站尚未取得证书原件，因此不把它写成认证结论',
      '量贩装与盒装的净含量、营养成分表数值不同（同一单袋规格下的标示值差异）待质检确认'
    ],
    detailPage: true
  },
  {
    id: 'shihu4',
    name: '铁皮石斛原浆饮（量贩装）',
    shortName: '铁皮石斛原浆量贩装',
    category: 'shihu',
    form: '袋装（量贩）',
    spec: ['净含量 900mL', '30mL × 30 袋', '内袋独立包装'],
    ingredients: '铁皮石斛浆、木糖醇',
    type: '植物饮料',
    shelfLife: '18 个月',
    storage: '干燥、通风的室内',
    origin: '云南省保山市',
    method: '开袋即饮。喝前摇一摇、撕开一小口；将内袋浸泡于热水中 3 分钟，加热后口感更佳（禁止微波炉加热）。',
    cautions: [
      '婴幼儿、孕妇、乳母不宜食用。',
      '若包装破损、胀袋请勿饮用。',
      '本品有少量沉淀属正常现象，请放心饮用，喝前摇一摇。',
      '本产品生产过程中 0% 添加且产品中未检测出防腐剂、蔗糖。'
    ],
    highlights: ['铁皮石斛浆含量 ≥90%', '云南芒市高黎贡山产区', '0 脂肪'],
    rawMaterials: ['云南芒市高黎贡山（包装正面与产品资料表述）'],
    labelFacts: [
      { label: '石斛多糖', value: '≥2000mg/100g（包装标示值）' },
      { label: '甘露糖', value: '≥1000mg/100g（包装标示值）' }
    ],
    images: [
      { src: `${LOCAL_IMAGE_BASE}/shihu4-front.jpg`, alt: '滋仙草铁皮石斛原浆饮量贩装正面', caption: '量贩装正面：净含量 900mL（30mL × 30）' },
      { src: `${LOCAL_IMAGE_BASE}/shihu4-label.jpg`, alt: '滋仙草铁皮石斛原浆饮量贩装信息面', caption: '量贩装信息面：配料、保质期、贮存条件与食用方法' }
    ],
    lead: '与铁皮石斛原浆盒装同配方的 900mL 量贩袋装，30 袋独立内袋，更耐存。',
    gaps: ['量贩装与盒装是否存在标签差异待质检确认'],
    detailPage: true
  }
];

/** @type {{ id: string, name: string }[]} */
export const productCategoryList = [
  { id: 'shihu', name: '石斛系列' },
  { id: 'tianma', name: '天麻系列' }
];

/** @type {string[]} */
export const factProductIds = productFacts.map((facts) => facts.id);

/**
 * @param {string} id
 * @returns {ProductFacts | null}
 */
export function findProductFacts(id) {
  return productFacts.find((facts) => facts.id === id) || null;
}

export default { productFacts, productCategoryList, factProductIds, findProductFacts };
